/**
 * Shared client for POST /api/embed/product-id/resolve.
 *
 * Used by both the SceneDesigner cart and the Viewer's SKU pill. Server-side
 * resolution is required because the canonical resolver honours per-combination
 * manual SKU overrides (a hash-keyed table); a client-side template substitution
 * would silently drop them. See DProductVariantService.GenerateProductIdAsync.
 *
 * Wire shape: caller mints opaque groupKeys to map results back to its own
 * row/instance structure. The endpoint echoes them verbatim.
 */

/** Request body for POST /api/embed/product-id/resolve. */
export interface ProductIdResolveRequest {
  items: ProductIdResolveItem[];
}

export interface ProductIdResolveItem {
  /** Opaque key the client made up — echoed back so the response maps to the row. */
  groupKey: string;
  productId: string;
  /** surfaceKey → material row id. Locked surfaces don't need to be sent. */
  materialPicks: Record<string, string>;
}

export interface ProductIdResolveResponse {
  results: ProductIdResolveItemResult[];
}

export interface ProductIdResolveItemResult {
  groupKey: string;
  /** Generated ProductID (template-resolved + SKU codes, or fixed). Null when failed. */
  productId: string | null;
  /** Short error label when generation failed. Null on success. */
  error: string | null;
}

export interface ResolveProductIdsOptions {
  baseUrl?: string;
  body: ProductIdResolveRequest;
  signal?: AbortSignal;
}

export async function resolveProductIds(opts: ResolveProductIdsOptions): Promise<ProductIdResolveResponse> {
  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const url = `${base}/api/embed/product-id/resolve`;
  const res = await fetch(url, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(opts.body),
    signal: opts.signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`resolveProductIds failed: ${res.status} ${res.statusText} ${text}`);
  }
  return (await res.json()) as ProductIdResolveResponse;
}
